(() => {
  'use strict';

  const ATT = [
    {
      key: 'perfusion',
      name: 'Perfusion',
      opts: [
        'Pink, moist membranes; CRT 1 to 2 sec; strong synchronous pulses; normal heart rate',
        'Slightly pale or hyperaemic membranes; CRT about 2 sec; mildly reduced or bounding pulses; mild tachycardia',
        'Pale or grey membranes; CRT over 2 sec; weak, thready pulses; moderate to severe tachycardia',
        'White, blue or muddy membranes; CRT over 3 sec or absent; absent peripheral pulses; severe tachycardia or bradycardia (decompensated shock)',
      ],
    },
    {
      key: 'cardiac',
      name: 'Cardiac',
      opts: [
        'Normal rate and rhythm; no murmur; no pulse deficits',
        'Mild tachycardia or bradycardia; no arrhythmia; no murmur',
        'Moderate rhythm disturbance; occasional arrhythmia or VPCs; murmur or pulse deficits present',
        'Severe or sustained arrhythmia; frequent pulse deficits; haemodynamically significant; cardiac arrest',
      ],
    },
    {
      key: 'respiratory',
      name: 'Respiratory',
      opts: [
        'Normal rate and effort; no stridor; no abdominal component',
        'Mild increase in rate or effort; mild abdominal component',
        'Moderate increase; open-mouth breathing; elbows abducted; orthopnoea',
        'Marked effort; gasping or agonal breathing; cyanosis',
      ],
    },
    {
      key: 'emi',
      name: 'Eye / Muscle / Integument',
      opts: [
        'No injury; skin, eyes and soft tissue intact',
        'Abrasions, superficial lacerations or contusions; mild soft-tissue injury',
        'Deep or multiple lacerations; moderate muscle or skin injury; corneal injury',
        'Major degloving; penetrating wound into a body cavity; globe rupture, proptosis or ocular avulsion',
      ],
    },
    {
      key: 'skeletal',
      name: 'Skeletal',
      opts: [
        'No injury; sound on all limbs',
        'Single closed fracture; mild lameness; minor skeletal injury',
        'Multiple closed fractures or a single open fracture; non-weight-bearing lameness',
        'Multiple open fractures; open joint; spinal fracture or luxation; limb-threatening skeletal injury',
      ],
    },
    {
      key: 'neuro',
      name: 'Neurologic',
      opts: [
        'Normal mentation; conscious, alert and aware; normal reflexes',
        'Dull or depressed but conscious; ambulatory but ataxic; abnormal reflexes',
        'Obtunded or stuporous; unconscious but responsive to noxious stimuli; nociception lost in one limb',
        'Comatose; non-responsive to all stimuli; refractory seizures; nociception lost in more than one limb or paralysis',
      ],
    },
  ];

  const MGCS = [
    {
      key: 'motor',
      name: 'Motor activity',
      opts: [
        'Normal gait, normal spinal reflexes',
        'Hemiparesis, tetraparesis or decerebrate activity',
        'Recumbent, intermittent extensor rigidity',
        'Recumbent, constant extensor rigidity',
        'Recumbent, constant extensor rigidity with opisthotonus',
        'Recumbent, muscle hypotonia, depressed or absent spinal reflexes',
      ],
    },
    {
      key: 'brainstem',
      name: 'Brainstem reflexes',
      opts: [
        'Normal pupillary light reflexes and oculocephalic reflexes',
        'Slow pupillary light reflexes, normal to reduced oculocephalic reflexes',
        'Bilateral unresponsive miosis, normal to reduced oculocephalic reflexes',
        'Pinpoint pupils, reduced to absent oculocephalic reflexes',
        'Unilateral unresponsive mydriasis, reduced to absent oculocephalic reflexes',
        'Bilateral unresponsive mydriasis, reduced to absent oculocephalic reflexes',
      ],
    },
    {
      key: 'consciousness',
      name: 'Level of consciousness',
      opts: [
        'Occasional periods of alertness, responsive to environment',
        'Depression or delirium, capable of responding but response may be inappropriate',
        'Semi-comatose, responsive to visual stimuli',
        'Semi-comatose, responsive to auditory stimuli',
        'Semi-comatose, responsive only to repeated noxious stimuli',
        'Comatose, unresponsive to repeated noxious stimuli',
      ],
    },
  ];

  const ATT_BANDS = {
    dog: [
      {
        max: 3,
        tone: 'low',
        label: 'Minor trauma',
        action: 'Stabilise, treat wounds and recheck. Lower observed mortality risk.',
      },
      {
        max: 8,
        tone: 'moderate',
        label: 'Serious',
        action: 'Active resuscitation and close monitoring are warranted.',
      },
      {
        max: 11,
        tone: 'high',
        label: 'High risk',
        action: 'Guarded prognosis. Aggressive stabilisation and close reassessment are warranted.',
      },
      {
        max: 18,
        tone: 'grave',
        label: 'Grave',
        action: 'Critical injury burden. Rapid intervention and a frank prognosis discussion are warranted.',
      },
    ],
    cat: [
      {
        max: 2,
        tone: 'low',
        label: 'Minor trauma',
        action: 'Stabilise and recheck. Lower observed mortality risk.',
      },
      {
        max: 5,
        tone: 'moderate',
        label: 'Severe trauma',
        action: 'Admit, stabilise and monitor intensively.',
      },
      {
        max: 9,
        tone: 'high',
        label: 'High risk',
        action: 'Aggressive stabilisation and close reassessment are warranted.',
      },
      {
        max: 18,
        tone: 'grave',
        label: 'Grave',
        action: 'Critical injury burden. Rapid intervention and a frank prognosis discussion are warranted.',
      },
    ],
  };

  const MGCS_BANDS = [
    {
      max: 8,
      tone: 'grave',
      label: 'Grave',
      action: 'Severe neurologic dysfunction; intensive management and serial reassessment are warranted.',
    },
    {
      max: 14,
      tone: 'moderate',
      label: 'Guarded',
      action: 'Guarded neurologic prognosis; monitor the trend closely.',
    },
    {
      max: 18,
      tone: 'low',
      label: 'Good',
      action: 'More favourable neurologic status; continue serial monitoring.',
    },
  ];

  class TraumaTriageCalculator extends HTMLElement {
    constructor() {
      super();

      this.state = {
        species: 'dog',
        head: 'off',
        att: {},
        mgcs: {},
      };

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.cacheElements();
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.buildCategories(this.attBody, ATT, 'att');
      this.buildCategories(this.mgcsBody, MGCS, 'mgcs');
      this.buildReference();
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.initialized = false;
    }

    cacheElements() {
      this.attBody = this.querySelector('[data-att-body]');
      this.mgcsBody = this.querySelector('[data-mgcs-body]');
      this.attCount = this.querySelector('[data-att-count]');
      this.mgcsCount = this.querySelector('[data-mgcs-count]');
      this.refTable = this.querySelector('[data-reference-table]');
      this.footnote = this.querySelector('[data-footnote]');
      this.band = this.querySelector('[data-band]');
      this.scoreNumber = this.querySelector('[data-score-number]');
      this.scoreLabel = this.querySelector('[data-score-label]');
      this.scoreOf = this.querySelector('[data-score-of]');
      this.meter = this.querySelector('[data-meter-fill]');
      this.chipAtt = this.querySelector('[data-chip-att]');
      this.chipMgcs = this.querySelector('[data-chip-mgcs]');
      this.chipMgcsWrap = this.querySelector('[data-chip-mgcs-wrap]');
      this.interpretation = this.querySelector('[data-interpretation]');
    }

    sum(store) {
      return Object.values(store).reduce((total, value) => total + value, 0);
    }

    count(store) {
      return Object.keys(store).length;
    }

    bandFor(total, bands) {
      return bands.find((band) => total <= band.max) || bands[bands.length - 1];
    }

    buildCategories(container, groups, type) {
      container.innerHTML = '';

      groups.forEach((group) => {
        const section = document.createElement('div');
        section.className = 'ttc-category';
        section.innerHTML = `
          <div class="ttc-category-title">
            <b>${group.name}</b>
            <span
              class="ttc-category-selected"
              data-selected-label="${type}-${group.key}"
            >
              Not scored
            </span>
          </div>
          <div class="ttc-options"></div>
        `;

        const options = section.querySelector('.ttc-options');
        const values = type === 'att' ? [0, 1, 2, 3] : [6, 5, 4, 3, 2, 1];

        values.forEach((value) => {
          const optionIndex = type === 'att' ? value : 6 - value;
          const label = document.createElement('label');

          label.className = 'ttc-option';
          label.innerHTML = `
            <input
              type="radio"
              name="${this.dataset.sectionId}-${type}-${group.key}"
              value="${value}"
              data-score-type="${type}"
              data-score-key="${group.key}"
            >
            <span class="ttc-option-number">${value}</span>
            <span class="ttc-option-description">${group.opts[optionIndex]}</span>
          `;

          options.appendChild(label);
        });

        container.appendChild(section);
      });
    }

    onClick(event) {
      const speciesButton = event.target.closest('button[data-species]');

      if (speciesButton) {
        this.state.species = speciesButton.dataset.species;
        this.setActive(
          speciesButton.closest('[data-species-segment]'),
          speciesButton,
        );
        this.buildReference();
        this.render();
        return;
      }

      const headButton = event.target.closest('button[data-head]');

      if (headButton) {
        this.state.head = headButton.dataset.head;
        this.classList.toggle('is-head-enabled', this.state.head === 'on');
        this.setActive(
          headButton.closest('[data-head-segment]'),
          headButton,
        );
        this.chipMgcsWrap.hidden = this.state.head !== 'on';
        this.buildReference();
        this.render();
        return;
      }

      if (event.target.closest('[data-reset]')) {
        this.reset();
        return;
      }

      const copyButton = event.target.closest('[data-copy]');

      if (copyButton) {
        this.copySummary(copyButton);
      }
    }

    onChange(event) {
      const input = event.target.closest('input[data-score-type]');

      if (!input) return;

      const scoreType = input.dataset.scoreType;
      const scoreKey = input.dataset.scoreKey;
      const store = this.state[scoreType];
      const category = input.closest('.ttc-category');

      store[scoreKey] = Number(input.value);

      category.querySelectorAll('.ttc-option').forEach((option) => {
        option.classList.toggle('is-selected', option.contains(input));
      });

      const selectedLabel = this.querySelector(
        `[data-selected-label="${scoreType}-${scoreKey}"]`,
      );

      if (selectedLabel) {
        selectedLabel.textContent = `Scored: ${input.value}`;
      }

      this.render();
    }

    setActive(container, activeButton) {
      container.querySelectorAll('button').forEach((button) => {
        const isActive = button === activeButton;

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    }

    reset() {
      this.state.att = {};
      this.state.mgcs = {};

      this.querySelectorAll('input[data-score-type]').forEach((input) => {
        input.checked = false;
      });

      this.querySelectorAll('.ttc-option').forEach((option) => {
        option.classList.remove('is-selected');
      });

      this.querySelectorAll('[data-selected-label]').forEach((label) => {
        label.textContent = 'Not scored';
      });

      this.render();
    }

    render() {
      const attScore = this.sum(this.state.att);
      const attCount = this.count(this.state.att);
      const mgcsScore = this.sum(this.state.mgcs);
      const mgcsCount = this.count(this.state.mgcs);

      this.attCount.textContent = `${attCount} / 6 scored`;
      this.mgcsCount.textContent = `${mgcsCount} / 3 scored`;
      this.chipAtt.textContent = attScore;
      this.chipMgcs.textContent = mgcsScore;
      this.chipMgcsWrap.hidden = this.state.head !== 'on';
      this.scoreNumber.innerHTML = `${attScore}<small> / 18</small>`;
      this.meter.style.width = `${Math.round((attScore / 18) * 100)}%`;

      if (attCount === 0) {
        this.paint('idle');
        this.scoreLabel.textContent = 'Start scoring';
        this.scoreOf.textContent = 'ATT severity';
        this.meter.style.width = '0%';
        this.interpretation.innerHTML = `
          <div style="color: var(--ttc-ink-soft)">
            Score all six ATT categories to generate a severity interpretation.
          </div>
        `;
        return;
      }

      if (attCount < 6) {
        const missingCount = 6 - attCount;
        const categoryWord = missingCount === 1 ? 'category' : 'categories';

        this.paint('idle');
        this.scoreLabel.textContent = 'Incomplete ATT score';
        this.scoreOf.textContent =
          this.state.species === 'dog'
            ? 'Dog, ATT severity'
            : 'Cat, ATT severity';
        this.interpretation.innerHTML = `
          <div class="ttc-interpretation-row">
            <span class="ttc-interpretation-key">ATT ${attScore}/18</span>
            <span class="ttc-interpretation-value">
              ${missingCount} ${categoryWord} still unscored. Complete all categories
              before interpreting prognosis.
            </span>
          </div>
        `;
        return;
      }

      const attBand = this.bandFor(
        attScore,
        ATT_BANDS[this.state.species],
      );

      this.paint(attBand.tone);
      this.scoreLabel.textContent = attBand.label;
      this.scoreOf.textContent =
        this.state.species === 'dog'
          ? 'Dog, ATT severity'
          : 'Cat, ATT severity';

      let interpretationHtml = `
        <div class="ttc-interpretation-row">
          <span class="ttc-interpretation-key">ATT ${attScore}/18</span>
          <span class="ttc-interpretation-value">${attBand.action}</span>
        </div>
      `;

      if (this.state.head === 'on') {
        if (mgcsCount < 3) {
          const missingCount = 3 - mgcsCount;
          const categoryWord = missingCount === 1 ? 'category' : 'categories';

          interpretationHtml += `
            <div class="ttc-interpretation-row">
              <span class="ttc-interpretation-key">MGCS</span>
              <span class="ttc-interpretation-value">
                ${missingCount} ${categoryWord} still unscored. Complete MGCS before
                interpreting neurologic prognosis.
              </span>
            </div>
          `;
        } else {
          const mgcsBand = this.bandFor(mgcsScore, MGCS_BANDS);

          interpretationHtml += `
            <div class="ttc-interpretation-row">
              <span class="ttc-interpretation-key">MGCS ${mgcsScore}/18</span>
              <span class="ttc-interpretation-value">
                <b>${mgcsBand.label}.</b> ${mgcsBand.action}
              </span>
            </div>
          `;
        }
      }

      this.interpretation.innerHTML = interpretationHtml;
    }

    paint(tone) {
      const colors = {
        low: ['var(--ttc-low)', '#157a52'],
        moderate: ['var(--ttc-moderate)', '#a2760a'],
        high: ['var(--ttc-high)', '#b8541a'],
        grave: ['var(--ttc-grave)', '#a52822'],
        idle: ['#8a99a2', '#5f7079'],
      };
      const selectedColors = colors[tone] || colors.idle;

      this.band.style.background = `linear-gradient(
        150deg,
        ${selectedColors[0]},
        ${selectedColors[1]}
      )`;
    }

    buildReference() {
      const bands = ATT_BANDS[this.state.species];
      const speciesLabel = this.state.species === 'dog' ? 'Dog' : 'Cat';
      let minimum = 0;
      let tableHtml = `
        <tr>
          <th>ATT total</th>
          <th>${speciesLabel} read</th>
        </tr>
      `;

      bands.forEach((band) => {
        tableHtml += `
          <tr>
            <td>
              <span
                class="ttc-dot"
                style="background: var(--ttc-${band.tone})"
              ></span>
              ${minimum} to ${band.max}
            </td>
            <td>${band.label}</td>
          </tr>
        `;
        minimum = band.max + 1;
      });

      if (this.state.head === 'on') {
        tableHtml += `
          <tr>
            <th>MGCS total</th>
            <th>Neurologic read</th>
          </tr>
          <tr>
            <td>3 to 8</td>
            <td>Grave</td>
          </tr>
          <tr>
            <td>9 to 14</td>
            <td>Guarded</td>
          </tr>
          <tr>
            <td>15 to 18</td>
            <td>Good</td>
          </tr>
        `;
      }

      this.refTable.innerHTML = tableHtml;
      this.footnote.innerHTML = `
        <b>Clinical use.</b> Higher ATT and lower MGCS scores are associated with
        worse outcomes. Use complete scores, re-score serially, and interpret the
        trend alongside examination findings, diagnostics and treatment response.
        This tool supports—not replaces—clinical judgement.
      `;
    }

    copySummary(button) {
      const attScore = this.sum(this.state.att);
      const attCount = this.count(this.state.att);
      const lines = [
        'TRAUMA TRIAGE SCORE',
        `Patient: ${this.state.species === 'dog' ? 'Dog' : 'Cat'}`,
        '',
      ];

      if (attCount === 6) {
        const attBand = this.bandFor(
          attScore,
          ATT_BANDS[this.state.species],
        );
        lines.push(`ATT total: ${attScore}/18 - ${attBand.label}`);
      } else {
        lines.push(
          `ATT total: ${attScore}/18 (${attCount}/6 categories scored) - incomplete`,
        );
      }

      ATT.forEach((category) => {
        if (this.state.att[category.key] !== undefined) {
          lines.push(`  ${category.name}: ${this.state.att[category.key]}`);
        }
      });

      if (this.state.head === 'on') {
        const mgcsScore = this.sum(this.state.mgcs);
        const mgcsCount = this.count(this.state.mgcs);

        lines.push('');

        if (mgcsCount === 3) {
          const mgcsBand = this.bandFor(mgcsScore, MGCS_BANDS);
          lines.push(`MGCS total: ${mgcsScore}/18 - ${mgcsBand.label}`);
        } else {
          lines.push(
            `MGCS total: ${mgcsScore}/18 (${mgcsCount}/3 scored) - incomplete`,
          );
        }

        MGCS.forEach((category) => {
          if (this.state.mgcs[category.key] !== undefined) {
            lines.push(
              `  ${category.name}: ${this.state.mgcs[category.key]}`,
            );
          }
        });
      }

      lines.push('', 'ASK A VET - Trauma triage tool');

      this.copyText(lines.join('\n')).then(() => {
        const originalText = button.textContent;

        button.textContent = 'Copied';
        button.classList.add('is-copied');

        window.setTimeout(() => {
          button.textContent = originalText;
          button.classList.remove('is-copied');
        }, 1600);
      });
    }

    async copyText(text) {
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          return;
        } catch (error) {
          // Fall back to document.execCommand below.
        }
      }

      const textarea = document.createElement('textarea');

      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }
  }

  if (!customElements.get('s-trauma-triage-calculator')) {
    customElements.define(
      's-trauma-triage-calculator',
      TraumaTriageCalculator,
    );
  }
})();
